import { z } from 'zod';
import { daysFromTodayUs, isUsCalendarDate } from '@/shared/infra/utils/calendarDate';
import { HIRE_DATE_MAX_DAYS_AHEAD } from '@/features/section-2/application/domain/entities/hire-date-window';

/**
 * Employer edit of a pending I-9's hire date and location (VTK-1954).
 *
 * Both fields are optional individually — an employer correcting only the location should not be
 * forced to re-enter a hire date — but the form must carry at least one change, or Save would be a
 * request that writes nothing.
 */
export const employmentDetailsSchema = z
  .object({
    hireDate: z
      .string()
      .trim()
      .optional()
      .refine((v) => !v || isUsCalendarDate(v), {
        message: 'Please enter a valid hire date (MM/DD/YYYY)',
      })
      .refine((v) => !v || (daysFromTodayUs(v) ?? 0) <= HIRE_DATE_MAX_DAYS_AHEAD, {
        message: `Hire date cannot be more than ${HIRE_DATE_MAX_DAYS_AHEAD} days in the future`,
      }),
    locationId: z.string().trim().optional(),
  })
  .refine((data) => !!data.hireDate || !!data.locationId, {
    message: 'Enter a hire date or choose a location',
    path: ['hireDate'],
  });

/**
 * Declared rather than `z.infer`red. ts-jest compiles with the root solution-style tsconfig, whose
 * compilerOptions omit `strict` — and without `strictNullChecks` zod resolves every field of a
 * conditional output type to optional, so an inferred type compiles in the app build but not under
 * test. Same reason `ReassignCompleterDialog` takes its form type from its port.
 */
export interface EmploymentDetailsFormData {
  hireDate?: string;
  locationId?: string;
}

export default employmentDetailsSchema;
